import { SelectProps } from '@mantine/core';
import { Badge } from 'src/design/atoms/Badge';
import { Box } from 'src/design/atoms/Box';
import { CloseButton } from 'src/design/atoms/CloseButton';
import { Group } from 'src/design/atoms/Group';
import { Combobox } from 'src/design/atoms/Combobox';
import { Select } from 'src/design/atoms/Select';
import { IconCheck } from '@tabler/icons-react';
import React from 'react';
import { UserDto } from 'src/api/FiretrolApiClient';
import { useUsersList } from 'src/api/queries';
import { BadgeShortList } from 'src/design/molecules/BadgeShortList';

export interface IUserSelectProps {
  onChange?: (value: UserDto | null) => void;
  dataFilter?: (options: UserDto[]) => UserDto[];
  value?: UserDto | null;
  hideLabel?: boolean;
}

/** @registry Single-select dropdown of users. */
export const UserSelect = React.memo<IUserSelectProps>(
  ({ onChange, value, dataFilter, hideLabel = false }) => {
    const { data: users = [] } = useUsersList();
    const userOptions = React.useMemo(
      () => (dataFilter ? dataFilter(users) : users),
      [users, dataFilter],
    );

    const mantineData = React.useMemo(
      () =>
        userOptions.map((user) => ({
          value: String(user.id),
          label: `${user.lastName}, ${user.firstName}`,
          user: user, // Store the full user object for access in renderOption
        })),
      [userOptions],
    );

    // Custom render function for Mantine Select options
    const renderOption: SelectProps['renderOption'] = React.useCallback(
      ({ option, checked }) => {
        const userOption = mantineData.find((d) => d.value === option.value);
        if (!userOption) return option.label;

        const user = userOption.user;
        return (
          <Box
            style={{
              width: '100%',
              borderLeft: checked
                ? '3px solid var(--mantine-color-blue-filled)'
                : '3px solid transparent',
              paddingLeft: '8px',
              transition: 'all 0.15s ease',
            }}
          >
            <Group
              justify="space-between"
              wrap="nowrap"
              style={{ width: '100%' }}
            >
              <Group gap="xs" wrap="nowrap">
                <span style={{ fontWeight: checked ? 600 : 400 }}>
                  {user.lastName}, {user.firstName}
                </span>
                {checked && (
                  <IconCheck
                    size={16}
                    style={{
                      color: 'var(--mantine-color-blue-filled)',
                      flexShrink: 0,
                    }}
                  />
                )}
              </Group>
              <div style={{ marginLeft: 'auto' }}>
                <BadgeShortList
                  items={
                    user.userOrganizationRole?.map((ur) => (
                      <Badge key={ur.organizationRole?.id} variant="light">
                        {ur.organizationRole?.name}
                      </Badge>
                    )) || []
                  }
                  maxItems={2}
                />
              </div>
            </Group>
          </Box>
        );
      },
      [mantineData],
    );

    const handleChange = React.useCallback(
      (selectedValue: string | null) => {
        if (!onChange) return;

        if (selectedValue === null) {
          onChange(null);
          return;
        }

        const selectedUser = userOptions.find(
          (user) => String(user.id) === selectedValue,
        );
        onChange(selectedUser || null);
      },
      [onChange, userOptions],
    );

    // Custom right section to show role badges and clear button
    const renderRightSection = () => {
      const hasClearButton = value !== null;
      return (
        <Group gap={4} wrap="nowrap" style={{ paddingRight: '4px' }}>
          {value && value.userOrganizationRole && (
            <div style={{ pointerEvents: 'none' }}>
              <BadgeShortList
                items={value.userOrganizationRole.map((ur) => (
                  <Badge
                    key={ur.organizationRole?.id}
                    variant="light"
                    size="sm"
                  >
                    {ur.organizationRole?.name}
                  </Badge>
                ))}
                maxItems={2}
              />
            </div>
          )}
          {hasClearButton && (
            <CloseButton
              size="sm"
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleChange(null);
              }}
              aria-label="Clear selection"
              style={{ pointerEvents: 'auto' }}
            />
          )}
          <Combobox.Chevron />
        </Group>
      );
    };

    return (
      <Select
        label={!hideLabel ? 'Users' : undefined}
        placeholder="Select a user..."
        data={mantineData}
        value={value ? String(value.id) : null}
        onChange={handleChange}
        searchable
        clearable={false} // handle clearing manually in rightSection
        renderOption={renderOption}
        rightSection={renderRightSection()}
        rightSectionWidth="auto"
        styles={{
          input: {
            width: '100%',
            minWidth: '100%',
          },
        }}
      />
    );
  },
);

UserSelect.displayName = 'UserSelect';
